/**
 * The logger is deliberately simple: four levels, structured payloads, and
 * a pluggable sink. The point is that every call site in the repo passes
 * through one of `logger.debug/info/warn/error` so that:
 *
 *   1. We can swap the transport without touching call sites (Sentry,
 *      OpenTelemetry, a file, or nothing at all in production).
 *   2. We can enforce a PII scrubber at a single chokepoint. Free-form
 *      `console.log(user)` is how invoice line-item descriptions
 *      accidentally end up in a third-party error tracker.
 *   3. CI can grep for `console.*` and fail the build — the only
 *      legitimate console calls live in this package's console sink.
 *
 * Keep the surface area tiny. Anything fancier (timers, counters, span
 * wrappers) belongs in a downstream package that depends on this one.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export const LOG_LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

/**
 * The record shape handed to a sink. Keep values JSON-serialisable; the
 * sink may forward these to a network endpoint where non-serialisable
 * values silently disappear.
 */
export interface LogRecord {
  readonly level: LogLevel;
  readonly message: string;
  readonly timestamp: string;
  readonly context?: Readonly<Record<string, unknown>>;
  /** Original error value for `logger.error(msg, err)`. Sinks are free
   * to extract `stack` / `cause` without further serialisation. */
  readonly error?: unknown;
}

/**
 * Pluggable transport. Sinks must never throw — a failing sink should
 * swallow its own error and at most delegate to another sink (e.g. the
 * console sink) for diagnostics. A throw here would crash the call
 * site, which is exactly what we're trying to avoid.
 */
export interface LogSink {
  write(record: LogRecord): void;
}

export interface Logger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, error?: unknown, context?: Record<string, unknown>): void;
  /**
   * Return a child logger that merges additional context into every
   * record. Useful for binding request / user / feature ids so we
   * don't repeat them at every call site.
   */
  child(context: Record<string, unknown>): Logger;
}

export interface LoggerConfig {
  readonly level: LogLevel;
  readonly sinks: readonly LogSink[];
  readonly scrub?: (context: Record<string, unknown>) => Record<string, unknown>;
  /** Clock indirection for deterministic tests. */
  readonly now?: () => Date;
}
